import { useState, useEffect, useRef } from "react";
import {
  HeadingDropdown,
  BoldButton,
  ItalicDropdown,
  TextColorDropdown,
  BackgroundColorDropdown,
  AlignmentDropdown,
  ListDropdown
} from "./BubbleMenuHelpers";
import { HighlightDropdown } from "./BubbleMenuHelpers/HighlightDropdown";
import { MagicHighlightButton } from "./BubbleMenuHelpers/MagicHighlightButton";
import { TextLinkWizard } from "./TextLinkWizard";
import { Button } from "@/components/ui/button";
import { Link2 } from "lucide-react";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useGlossaryDataContext } from "@/providers/ObjectsDataProvider";
import { getCachedTemplates } from "@/lib/stores/templatesStore";
import { GlossaryBlueprint } from "@/types-games";
import { useToast } from "@/hooks/use-toast";
import { useZoom } from "@/providers/ZoomProvider";

interface BubbleMenuProps {
  isVisible: boolean;
  position: { x: number; y: number };
  onFormat: (command: string, value?: string) => void;
  selectedText?: string; // Add selected text for highlighting
}

export const BubbleMenu = ({ isVisible, position, onFormat, selectedText }: BubbleMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const { selectedGame } = useSimplifiedUserData();
  const { createGlossary, updateGlossary, loadData } = useGlossaryDataContext();
  const { toast } = useToast();
  const { zoom } = useZoom();
  
  const [isLinkWizardOpen, setIsLinkWizardOpen] = useState(false);

  useEffect(() => {
    if (isVisible && menuRef.current) {
      const menu = menuRef.current;
      const rect = menu.getBoundingClientRect();
      
      // Adjust position if menu goes outside viewport
      // Divide by zoom to compensate for container zoom
      let adjustedX = position.x / zoom;
      let adjustedY = position.y / zoom;
      
      const viewportWidth = window.innerWidth / zoom;
      const viewportHeight = window.innerHeight / zoom;
      const menuWidth = rect.width / zoom;
      const menuHeight = rect.height / zoom;
      
      if (adjustedX + menuWidth > viewportWidth) {
        adjustedX = viewportWidth - menuWidth - 10;
      }
      
      if (adjustedY + menuHeight > viewportHeight) {
        adjustedY = (position.y / zoom) - menuHeight - 10;
      }
      
      menu.style.left = `${adjustedX}px`;
      menu.style.top = `${adjustedY}px`;
    }
  }, [isVisible, position, zoom]);

  const handleOpenLinkWizard = () => {
    if (!selectedText || !selectedGame) return;
    setIsLinkWizardOpen(true);
  };

  const handleCreateLibraryObject = async (data: {
    templateId: string;
    folderId: string | null;
    name: string;
    selectedText: string;
  }) => {
    if (!selectedGame) return;

    try {
      // Get the template
      const template = getCachedTemplates(selectedGame.id).find(
        (t: GlossaryBlueprint) => t.id === data.templateId
      );
      
      if (!template) {
        toast({
          title: "Error",
          description: "Template not found",
          variant: "destructive",
        });
        return;
      }

      // Create the object with the template structure
      // Initialize data with template structure but empty values
      const initialData = Array.isArray(template.data) 
        ? JSON.parse(JSON.stringify(template.data)) 
        : [];

      const newObject = await createGlossary({
        game_id: selectedGame.id,
        name: data.name,
        type: template.type || 'Library Object',
        tags: template.tags || [],
        identifiers: [],
        data: initialData,
        blueprint_id: template.id,
        blueprint_version: 0,
        folder: data.folderId,
      });

      // Apply highlighting to the selected text
      onFormat('highlight', JSON.stringify({
        type: 'library-object',
        id: newObject.id,
        keys: [data.selectedText]
      }));

      toast({
        title: "Success",
        description: "Library object created and text highlighted",
      });

      await loadData();
    } catch (error) {
      console.error("Error creating library object:", error);
      toast({
        title: "Error",
        description: "Failed to create library object",
        variant: "destructive",
      });
    }
  };

  const handleLinkToValue = async (data: {
    objectId: string;
    fieldId: string;
    selectedText: string;
  }) => {
    if (!selectedGame) return;

    try {
      // Get the object and update the field value
      const { getCachedObjects } = await import("@/lib/stores/objectsStore");
      const objects = getCachedObjects(selectedGame.id);
      const object = objects.find((obj: any) => obj.id === data.objectId);

      if (!object) {
        toast({
          title: "Error",
          description: "Object not found",
          variant: "destructive",
        });
        return;
      }

      // Update the field value
      const updatedData = JSON.parse(JSON.stringify(object.data));
      let fieldUpdated = false;

      updatedData.forEach((section: any) => {
        section.fields.forEach((field: any) => {
          if (field.id === data.fieldId) {
            // Handle multiple values
            if (field.multiple && Array.isArray(field.value)) {
              field.value.push(data.selectedText);
            } else if (field.multiple) {
              field.value = [data.selectedText];
            } else {
              field.value = data.selectedText;
            }
            fieldUpdated = true;
          }
        });
      });

      if (!fieldUpdated) {
        toast({
          title: "Error",
          description: "Field not found",
          variant: "destructive",
        });
        return;
      }

      await updateGlossary({
        id: object.id,
        game_id: object.game_id,
        name: object.name,
        type: object.type || 'Library Object',
        data: updatedData,
        tags: object.tags || [],
        blueprint_id: object.blueprint_id,
        identifiers: object.identifiers,
        folder: object.folder,
      });

      // Apply highlighting to the selected text
      onFormat('highlight', JSON.stringify({
        type: 'linked-value',
        id: `${data.objectId}_${data.fieldId}`,
        keys: [data.selectedText]
      }));

      toast({
        title: "Success",
        description: "Text linked to field and highlighted",
      });

      await loadData();
    } catch (error) {
      console.error("Error linking to value:", error);
      toast({
        title: "Error",
        description: "Failed to link text to field",
        variant: "destructive",
      });
    }
  };

  if (!isVisible) return null;

  // Prevent the menu from stealing focus from the editor
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  return (
    <>
      <div
        ref={menuRef}
        className="fixed bg-popover border border-border rounded-sm p-1 flex gap-1 animate-in fade-in-0 zoom-in-95 duration-200 shadow-lg"
        style={{ left: position.x / zoom, top: position.y / zoom, zIndex: 10001 }}
        onMouseDown={handleMouseDown}
      >
        <HeadingDropdown onFormat={onFormat} />
        <BoldButton onFormat={onFormat} />
        <ItalicDropdown onFormat={onFormat} />
        <TextColorDropdown onFormat={onFormat} />
        <BackgroundColorDropdown onFormat={onFormat} />
        <AlignmentDropdown onFormat={onFormat} />
        <ListDropdown onFormat={onFormat} />
        
        {/* Only show highlight options when text is selected */}
        {selectedText && (
          <>
           {/*  <HighlightDropdown onFormat={onFormat} selectedText={selectedText} />
            <MagicHighlightButton onFormat={onFormat} selectedText={selectedText} /> */}
            
            {/* Text Link Button */}
            {/* <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-primary/10"
              title="Link text to library object"
              onClick={handleOpenLinkWizard}
            >
              <Link2 size={14} className="text-primary" />
            </Button> */}
          </>
        )}
      </div>

      {/* Text Link Wizard */}
      {selectedText && (
        <TextLinkWizard
          isOpen={isLinkWizardOpen}
          onClose={() => setIsLinkWizardOpen(false)}
          selectedText={selectedText}
          onCreateLibraryObject={handleCreateLibraryObject}
          onLinkToValue={handleLinkToValue}
        />
      )}
    </>
  );
}; 